// src/pages/pharmacy/PrescriptionVerification.js
import React, { useState, useEffect } from 'react';
import { 
  FileCheck, AlertCircle, X, CheckCircle, Eye, 
  Download, Clock, User, Phone, Mail, Package,
  Shield, XCircle
} from 'lucide-react';
import pharmacyService from '../../services/pharmacyService';

const PrescriptionVerification = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => {
    fetchPrescriptionOrders();
  }, [filterStatus]);

  const fetchPrescriptionOrders = async () => {
    try {
      setLoading(true);
      const response = await pharmacyService.getOrders({
        requiresPrescription: true,
        prescriptionVerified: filterStatus === 'verified',
        status: filterStatus === 'pending' ? 'pending,confirmed' : undefined
      });

      if (response.success) {
        // Filter orders that have prescriptions
        const ordersWithPrescriptions = response.data.filter(
          order => order.prescription && order.prescription.images && order.prescription.images.length > 0
        );
        setOrders(ordersWithPrescriptions);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPrescription = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleVerifyPrescription = async (orderId, isApproved, note) => {
    try {
      setVerifying(true);
      const response = await pharmacyService.verifyPrescription(orderId, {
        isApproved,
        note
      });

      if (response.success) {
        setShowModal(false);
        setSelectedOrder(null);
        await fetchPrescriptionOrders();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Prescription Verification</h1>
        <p className="text-gray-600 mt-1">Review and verify customer prescriptions</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-5 h-5 text-red-600" />
            </button>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex gap-4">
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'pending'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Pending Verification
          </button>
          <button
            onClick={() => setFilterStatus('verified')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'verified'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Verified
          </button>
        </div>
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FileCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {filterStatus === 'pending' 
              ? 'No Prescriptions to Verify' 
              : 'No Verified Prescriptions'}
          </h3>
          <p className="text-gray-600">
            {filterStatus === 'pending'
              ? 'Prescriptions requiring verification will appear here'
              : 'Verified prescriptions will appear here'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {orders.map((order) => (
            <div
              key={order._id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Order #{order.orderNumber}
                    </h3>
                    {order.prescription?.isVerified ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                        <CheckCircle size={14} />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        <Clock size={14} />
                        Pending
                      </span>
                    )}
                  </div>

                  {/* Customer Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User size={16} />
                      <span>{order.customer?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone size={16} />
                      <span>{order.customer?.phone || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail size={16} />
                      <span>{order.customer?.email || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Package size={16} />
                  Ordered Items
                </h4>
                <div className="space-y-2">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">
                        {item.medicine?.name || 'Medicine'} × {item.quantity}
                      </span>
                      {item.prescriptionRequired && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                          Rx Required
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Prescription Info */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Prescription Details</h4>
                <div className="text-sm space-y-1">
                  <p className="text-gray-600">
                    Uploaded: {new Date(order.createdAt).toLocaleString()}
                  </p>
                  {order.prescription?.verifiedAt && (
                    <p className="text-gray-600">
                      Verified: {new Date(order.prescription.verifiedAt).toLocaleString()}
                    </p>
                  )}
                  {order.prescription?.images && (
                    <p className="text-gray-600">
                      Attachments: {order.prescription.images.length} file(s)
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleViewPrescription(order)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Eye size={18} />
                  View Prescription
                </button>
                {!order.prescription?.isVerified && (
                  <button
                    onClick={() => handleViewPrescription(order)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Shield size={18} />
                    Verify Now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Prescription Modal */}
      {showModal && selectedOrder && (
        <PrescriptionModal
          order={selectedOrder}
          onClose={() => {
            setShowModal(false);
            setSelectedOrder(null);
          }}
          onVerify={handleVerifyPrescription}
          verifying={verifying}
        />
      )}
    </div>
  );
};

// Prescription Modal Component
const PrescriptionModal = ({ order, onClose, onVerify, verifying }) => {
  const [note, setNote] = useState('');
  const [action, setAction] = useState(null);

  const handleSubmit = () => {
    if (action) {
      onVerify(order._id, action === 'approve', note);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Review Prescription</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Order Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Order Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Order Number</p>
                <p className="font-medium">{order.orderNumber}</p>
              </div>
              <div>
                <p className="text-gray-600">Customer</p>
                <p className="font-medium">{order.customer?.name}</p>
              </div>
              <div>
                <p className="text-gray-600">Phone</p>
                <p className="font-medium">{order.customer?.phone}</p>
              </div>
              <div>
                <p className="text-gray-600">Date</p>
                <p className="font-medium">{new Date(order.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Prescription Images */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Prescription Images</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {order.prescription?.images?.map((image, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-2">
                  <img
                    src={image}
                    alt={`Prescription ${index + 1}`}
                    className="w-full h-64 object-contain rounded"
                  />
                  <div className="mt-2 flex justify-end">
                    <a
                      href={image}
                      download={`prescription-${order.orderNumber}-${index + 1}`}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Download size={14} />
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Verification Note */}
          {!order.prescription?.isVerified && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Add any notes about the prescription..."
              />
            </div>
          )}

          {/* Action Buttons */}
          {!order.prescription?.isVerified && (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setAction('approve');
                  handleSubmit();
                }}
                disabled={verifying}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {verifying && action === 'approve' ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Approving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    <span>Approve Prescription</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setAction('reject');
                  handleSubmit();
                }}
                disabled={verifying}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {verifying && action === 'reject' ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Rejecting...</span>
                  </>
                ) : (
                  <>
                    <XCircle size={20} />
                    <span>Reject Prescription</span>
                  </>
                )}
              </button>
            </div>
          )}

          {order.prescription?.isVerified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">Prescription Verified</p>
                <p className="text-sm text-green-700">
                  Verified on {new Date(order.prescription.verifiedAt).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionVerification;